import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Power, PowerOff, RotateCcw, Mail, UserCog, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", code: "", address: "", contact_email: "", contact_phone: "", logo_url: "", branch_limit: 5, user_limit: 50 };

export default function SuperAdminHospitals() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create, otherwise the hospital being edited
  const [form, setForm] = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);

  // Manage-admins drawer state
  const [admins, setAdmins] = useState(null); // { hospital, users, loading, busy_id }
  const openAdmins = async (h) => {
    setAdmins({ hospital: h, users: [], loading: true, busy_id: null });
    try {
      const { data } = await api.get(`/superadmin/hospitals/${h.id}/users`);
      setAdmins({ hospital: h, users: data.items || [], loading: false, busy_id: null });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load users");
      setAdmins(null);
    }
  };
  const promoteToAdmin = async (u) => {
    if (!admins) return;
    const verb = u.role === "hospital_admin" ? "re-confirm" : "promote";
    if (!window.confirm(`${verb === "promote" ? "Promote" : "Re-activate"} ${u.email} to Hospital Admin?`)) return;
    setAdmins({ ...admins, busy_id: u.id });
    try {
      await api.post(`/superadmin/hospitals/${admins.hospital.id}/users/${u.id}/promote-admin`);
      toast.success(`${u.email} is now Hospital Admin.`);
      // refresh list
      const { data } = await api.get(`/superadmin/hospitals/${admins.hospital.id}/users`);
      setAdmins({ ...admins, users: data.items || [], busy_id: null });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Promote failed");
      setAdmins({ ...admins, busy_id: null });
    }
  };

  const load = async () => {
    const { data } = await api.get(`/superadmin/hospitals${showDeleted ? "?include_deleted=true" : ""}`);
    setItems(data.items);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showDeleted]);

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (h) => {
    setEditing(h);
    setForm({
      name: h.name || "", code: h.code || "", address: h.address || "",
      contact_email: h.contact_email || "", contact_phone: h.contact_phone || "",
      logo_url: h.logo_url || "",
      branch_limit: h.branch_limit || 5, user_limit: h.user_limit || 50,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/superadmin/hospitals/${editing.id}`, form);
        toast.success("Hospital updated");
      } else {
        await api.post("/superadmin/hospitals", form);
        toast.success("Hospital created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const toggle = async (h, status) => {
    try {
      await api.patch(`/superadmin/hospitals/${h.id}/status`, { status });
      toast.success(status === "active" ? "Activated" : "Suspended");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/superadmin/hospitals/${confirmDel.id}`);
      toast.success("Hospital deleted");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const reactivate = async (h) => {
    try {
      await api.post(`/superadmin/hospitals/${h.id}/reactivate`);
      toast.success(`${h.name} reactivated`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const sendResetLink = async (h) => {
    if (!h.contact_email) { toast.error(`No contact email on file for ${h.name}.`); return; }
    if (!window.confirm(`Send password reset link to ${h.contact_email}?`)) return;
    try {
      const { data } = await api.post(`/password/superadmin/send-reset-by-email`, { email: h.contact_email });
      if (data?.dev_link) {
        // Stub mode — surface link inline so the super admin can hand it over
        toast.success(`Dev mode: link copied to clipboard. ${data.dev_link.slice(0, 60)}…`);
        try { await navigator.clipboard?.writeText(data.dev_link); } catch { /* ignore */ }
      } else {
        toast.success(data?.message || `Reset link emailed to ${h.contact_email}`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not send reset link"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">SaaS Console</div>
          <h1 className="font-heading text-3xl font-semibold">Hospitals</h1>
          <p className="mt-1 text-sm text-ink-muted">{items.length} tenant{items.length === 1 ? "" : "s"}{showDeleted ? " (including deleted)" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 rounded-md border border-border bg-white px-3 py-2 text-xs text-ink-muted">
            <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="sa-show-deleted-toggle" />
            Show deleted
          </label>
          <Button onClick={openCreate} className="bg-brand hover:bg-brand-700" data-testid="sa-new-hospital"><Plus size={14} className="mr-2" />Add hospital</Button>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-4 py-3">Logo</th>
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Limits</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map(h => (
              <tr key={h.id} className="hover:bg-secondary/30" data-testid={`hospital-row-${h.code}`}>
                <td className="px-4 py-3">
                  {h.logo_url
                    ? <img src={h.logo_url} alt={h.name} className="h-8 w-8 rounded object-cover" />
                    : <div className="grid h-8 w-8 place-items-center rounded bg-brand/10 text-[10px] font-semibold text-brand">{(h.code || "?").slice(0, 2)}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{h.code}</td>
                <td className="px-4 py-3 font-medium">
                  {h.name}
                  {h.address && <div className="text-xs font-normal text-ink-muted">{h.address}</div>}
                </td>
                <td className="px-4 py-3 text-xs">
                  <div>{h.contact_email || "—"}</div>
                  <div className="text-ink-muted">{h.contact_phone || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs text-ink-muted">
                  Branches ≤ {h.branch_limit} · Users ≤ {h.user_limit}
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={
                    h.status === "active" ? "border-leaf/40 bg-leaf-50 text-leaf-700"
                    : h.status === "suspended" ? "border-amber-200 bg-amber-50 text-amber-700"
                    : h.status === "deleted" ? "border-rose-300 bg-rose-100 text-rose-800"
                    : "border-rose-200 bg-rose-50 text-rose-700"}>{h.status}</Badge>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    {h.status === "deleted" ? (
                      <Button size="sm" variant="ghost" onClick={() => reactivate(h)} title="Reactivate hospital" data-testid={`hospital-reactivate-${h.code}`}>
                        <RotateCcw size={14} className="text-leaf" />
                        <span className="ml-1 text-xs text-leaf">Reactivate</span>
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openAdmins(h)} title="Manage admins (promote / re-assign hospital_admin)" data-testid={`hospital-manage-admins-${h.code}`}>
                          <UserCog size={14} className="text-indigo-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => sendResetLink(h)} title="Send password reset link to hospital contact email" data-testid={`hospital-send-reset-${h.code}`}>
                          <Mail size={14} className="text-brand" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(h)} title="Edit hospital" data-testid={`hospital-edit-${h.code}`}><Pencil size={14} /></Button>
                        {h.status === "active"
                          ? <Button size="sm" variant="ghost" onClick={() => toggle(h, "suspended")} title="Suspend" data-testid={`hospital-suspend-${h.code}`}><PowerOff size={14} /></Button>
                          : <Button size="sm" variant="ghost" onClick={() => toggle(h, "active")} title="Activate" data-testid={`hospital-activate-${h.code}`}><Power size={14} className="text-leaf" /></Button>}
                        <Button size="sm" variant="ghost" onClick={() => setConfirmDel(h)} title="Delete" data-testid={`hospital-delete-${h.code}`}><Trash2 size={14} className="text-rose-500" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-ink-muted">No hospitals yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit hospital" : "New hospital / tenant"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div><Label>Name</Label><Input value={form.name} onChange={e => set("name", e.target.value)} data-testid="sa-hosp-name" /></div>
            <div><Label>Code</Label><Input value={form.code} onChange={e => set("code", e.target.value.toUpperCase())} placeholder="e.g. ARV" data-testid="sa-hosp-code" /></div>
            <div>
              <Label>Logo (JPG or PNG · auto-resized to fit OPD card)</Label>
              <div className="mt-2 flex items-center gap-3">
                {form.logo_url
                  ? <img src={form.logo_url} alt="logo" className="h-14 w-14 rounded border border-border object-contain bg-white" />
                  : <div className="grid h-14 w-14 place-items-center rounded border border-border bg-secondary text-[10px] font-semibold text-ink-muted">No logo</div>}
                <Input
                  type="file" accept="image/jpeg,image/png" data-testid="sa-hosp-logo-file"
                  onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return;
                    const fd = new FormData(); fd.append("file", f);
                    try {
                      const tid = editing?.id ? `&target_id=${editing.id}` : "";
                      const { data } = await api.post(`/files/upload-logo?scope=hospital${tid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                      set("logo_url", data.url);
                      toast.success(`Logo uploaded (${data.width}×${data.height})`);
                    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
                  }}
                />
                {form.logo_url && (
                  <Button
                    type="button" variant="outline" size="sm"
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 border-rose-200"
                    onClick={() => set("logo_url", "")}
                    data-testid="sa-hosp-logo-remove"
                  >
                    Remove logo
                  </Button>
                )}
              </div>
              <div className="mt-1 text-[11px] text-ink-muted">Max 5 MB · auto-resized to 256×256 max while keeping aspect ratio. Click <b>Remove logo</b> to clear, then Save.</div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={e => set("address", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Contact email</Label><Input value={form.contact_email} onChange={e => set("contact_email", e.target.value)} /></div>
              <div><Label>Contact phone</Label><Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Branch limit</Label><Input type="number" value={form.branch_limit} onChange={e => set("branch_limit", Number(e.target.value))} /></div>
              <div><Label>User limit</Label><Input type="number" value={form.user_limit} onChange={e => set("user_limit", Number(e.target.value))} /></div>
            </div>
            <Button onClick={save} className="bg-brand hover:bg-brand-700" data-testid="sa-hosp-save">{editing ? "Save changes" : "Create"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-rose-700">Delete this hospital?</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">This will <b>soft-delete</b> <b>{confirmDel?.name}</b> ({confirmDel?.code}). The hospital and all its branches/users will be hidden everywhere, but records remain in DB for audit/recovery.</p>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
              <Button onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="sa-hosp-delete-confirm">Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!admins} onOpenChange={(o) => !o && setAdmins(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserCog size={16} className="text-indigo-600" /> Manage admins · {admins?.hospital?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              <b>Use this to fix a tenant locked out of admin access</b> — e.g. the hospital admin demoted themselves to doctor by mistake. Click <b>Make Admin</b> on any user below to set their role to <code>hospital_admin</code>.
            </div>
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-left text-[10px] uppercase tracking-wider text-ink-muted">
                  <tr>
                    <th className="px-3 py-2">User</th>
                    <th className="px-3 py-2">Current role</th>
                    <th className="px-3 py-2">Status</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {admins?.loading && <tr><td colSpan={4} className="px-3 py-6 text-center text-ink-muted">Loading users…</td></tr>}
                  {!admins?.loading && (admins?.users || []).length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-ink-muted">No users found.</td></tr>}
                  {!admins?.loading && (admins?.users || []).map((u) => (
                    <tr key={u.id} data-testid={`manage-admin-row-${u.email}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{u.name || "—"}</div>
                        <div className="text-[11px] text-ink-muted">{u.email}</div>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={u.role === "hospital_admin" ? "border-leaf/40 bg-leaf-50 text-leaf-700" : "border-slate-200 bg-slate-50 text-slate-700"}>
                          {u.role}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={u.status === "active" ? "border-leaf/40 bg-leaf-50 text-leaf-700" : "border-rose-200 bg-rose-50 text-rose-700"}>
                          {u.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {u.role === "hospital_admin" && u.status === "active" ? (
                          <span className="text-[11px] text-leaf-700"><ShieldCheck size={11} className="mr-1 inline" /> Already admin</span>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => promoteToAdmin(u)}
                            disabled={admins.busy_id === u.id}
                            className="bg-brand hover:bg-brand-700"
                            data-testid={`manage-admin-promote-${u.email}`}
                          >
                            {admins.busy_id === u.id
                              ? <><Loader2 size={12} className="mr-2 animate-spin" /> Promoting…</>
                              : <><ShieldCheck size={12} className="mr-1.5" /> Make Admin</>}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
